/**
 * «Путь пешки» 2.0 — contracts v0 (docs/PATH-V2.md §12.2, docs/path-v2/CONTRACTS.md).
 *
 * What goes over the network (results, the progress view, feature flags) and what several packages draw or check
 * (the board sign language §9.8, task kinds and rules §6.2). A contract is changed here, never redefined locally;
 * every wire type has a zod twin in apps/server/src/schemas.path.ts checked with `Mutual`.
 *
 * IDs are stable and semantic (§12.1 p.2): a skill is 'gift', a step 'gifts-intro' — never '1.2.1'. Every released id
 * is kept in packages/content/src/path/registry.lock.json; renames go through `aliases`.
 */
import type { CoachStyle, Color, PersonaId, PieceType, Square } from './contracts.ts';

// ───────────────────────── ids ─────────────────────────

/** 'reach' | 'guard' | 'values' | 'gift' | 'save' | 'safe' | 'trade' | 'check' | 'routine' | … (kebab/camel Latin). */
export type SkillId = string;
/** A chapter: 'gifts', 'who-reaches' … */
export type ChapterId = string;
/** A step (a circle on the road): 'gifts-intro', 'gifts-not', 'gifts-minigame' … */
export type StepId = string;
/** Stage 0..10 (0 = «как ходят фигуры», 10 = the queen). */
export type PathStage = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/** Task difficulty (§3.10): 1 = few pieces + support, 2 = more pieces without support, 3 = has a «двойник». */
export type TaskTier = 1 | 2 | 3;

/** Content status (§8.6): no committed `published` flag. */
export type ContentStatus = 'draft' | 'playtest' | 'frozen';

/**
 * Help in path games (§7.1) = the existing `CoachStyle` minus the exam: 'teacher' «Учитель», 'quiet' «тихий учитель»
 * (new: no advice arrows, only danger / gift moments, delayed, ≤ 15 lines a game), 'helper' «Подсказчик».
 * The exam is a separate mode (`CoachStyle` 'exam').
 */
export type HelpLevel = Exclude<CoachStyle, 'exam'>;

// ───────────────────────── board sign language (§9.8) ─────────────────────────

/**
 * One sign of the StageBoard SVG layer. Only position and opacity animate; dashes are static; no filters.
 * White is always at the bottom. At most two sign kinds at once (§3.1 p.4) — the lesson runner enforces it.
 */
export type StageSign =
  /** «достаёт» — a yellow line from a piece to a square it reaches */
  | { kind: 'reach'; from: Square; to: Square }
  /** «охраняет» — a blue shield on a guarded piece (`by` = optional guard squares, drawn as thin blue lines) */
  | { kind: 'guard'; square: Square; by?: Square[] }
  /** «подарок» — 🎁 above a piece */
  | { kind: 'gift'; square: Square }
  /** own piece under attack — a red dot */
  | { kind: 'danger'; square: Square }
  /** advice / the right move — a green arrow */
  | { kind: 'advice'; from: Square; to: Square }
  /** a proposed move, a choice option (numbered), a played move in a review — a dashed arrow */
  | { kind: 'dashed'; from: Square; to: Square; n?: 1 | 2 | 3 }
  /** the opponent's reply, a consequence — a red arrow */
  | { kind: 'reply'; from: Square; to: Square }
  /** a mini-game target */
  | { kind: 'flag'; square: Square }
  /** not relevant — dimmed squares */
  | { kind: 'dim'; squares: Square[] }
  /** the bot's move (thinking habit §7.4): the piece glows; `strength` 0..1 fades game by game */
  | { kind: 'glow'; square: Square; strength: number };

export type StageSignKind = StageSign['kind'];

// ───────────────────────── task kinds & rules (§6.2) ─────────────────────────

export type TaskKind = 'show' | 'tap' | 'move' | 'choose' | 'yesno' | 'count' | 'flags' | 'goal' | 'puzzle' | 'teachme' | 'own';

/** What a `tap` asks for — the answer is computed from the position by @gambit/core, never typed as squares only. */
export type TapRule =
  /** every gift of the side to move (core `giftSquares`) */
  | { rule: 'gift' }
  /** every opponent piece the piece on `from` reaches */
  | { rule: 'reaches'; from: Square }
  /** every piece that guards the piece on `square` (direct guards) */
  | { rule: 'guards'; square: Square }
  /** v1 «Что он хочет?»: every piece of the side to move the opponent piece on `from` (it just moved there) wins as a gift */
  | { rule: 'threat'; from: Square }
  /** explicit squares (a fallback for shows; the truth check still runs where it can) */
  | { rule: 'squares'; squares: Square[] };

/** What a `move` must achieve — any legal move that meets the goal is accepted. */
export type MoveRule =
  | { rule: 'takesGift' }
  | { rule: 'mate' }
  /** the piece on `square` is no longer under attack and not lost (moved away / guarded / blocked) */
  | { rule: 'saves'; square: Square }
  | { rule: 'safeCheck' }
  /** v1: the side to move is in check — any answer (move / block / take) after which no own piece is lost */
  | { rule: 'escapesCheck' }
  /** a capture worth making (ch. 1.4 «Размен»): we take not less than we give — core `tradeBalance` ≥ 0 (a gift too) */
  | { rule: 'goodTrade' }
  /** explicit moves (UCI) */
  | { rule: 'uci'; moves: string[] };

/**
 * What a 👍/👎 question is about — the answer is computed for the dashed `move`. v1: 'goodTrade' — the capture takes no
 * less than it gives back (core `tradeBalance` ≥ 0, exchange 1/3/3/5/9, ch. 1.4 «Выгодно?»); 'threat' — `move` is the
 * opponent's LAST move, already on the board (not legal in the fen): does the piece it moved now win one of ours as a gift?
 */
export type YesNoRule = { rule: 'isGift' | 'isSafe' | 'isMate' | 'goodTrade' | 'threat'; move: string };

/** What a `choose` asks (v1): a 👍/👎 rule per option, or 'mostValuable' — the option taking the most valuable piece (1/3/3/5/9). */
export type ChooseRule = { rule: Exclude<YesNoRule['rule'], 'threat'> | 'mostValuable' };

/** Why an answer is wrong (§6.3) — each has a voice pool `v3.path.why.<reason>`. */
export type MistakeReason =
  | 'notReach' // «не достаёт»
  | 'notReachThis' // «эта не достаёт — достаёт другая наша» — a reaches tap on a piece another own piece reaches (chapter 1.1)
  | 'guardedByPawn' // «охраняет пешка»
  | 'guarded' // «охраняет …» (not a pawn)
  | 'weGiveToo' // «мы тоже отдаём»
  | 'notMate' // «не мат — король уходит»
  | 'notCheck' // «не шах»
  | 'willBeTaken' // «эту фигуру заберут»
  | 'kingCantTake' // «королю не взять — охраняют»
  | 'leftUnguarded' // «король ушёл — фигуру некому охранять»: a king step away from an own piece it guarded, now a gift (ch. 1.5)
  | 'missedGift' // 👎 on a real gift («это подарок — его никто не охраняет»)
  | 'cheaper' // v1 «кто дороже»: the chosen piece is cheaper («эта фигура дешевле», 1/3/3/5/9, chapter 1.1)
  | 'badTrade' // v1: a capture that gives more than it takes («отдаём дороже, чем забираем», ch. 1.4)
  | 'fairTrade' // v1: 👎 on a trade that takes no less than we give (not a gift: that one is 'missedGift')
  | 'missedSafe' // 👎 on a safe move («ход безопасный — даром ничего не заберут»)
  | 'notThreat' // «что он хочет?» (oppIdea): a piece of ours the opponent's moved piece does not reach
  | 'threatMissed' // «он хочет забрать?» (GEN `threat` yes/no): 👎 though his moved piece now wins ours as a gift
  | 'threatGuarded' // «что он хочет?» (GEN `threat`): his moved piece reaches this piece of ours, but it is guarded — not free for HIM
  | 'threatNotFree' // «что он хочет?» (GEN `threat`): he reaches it unguarded, but taking it loses him material (poisoned for him)
  | 'threatNone' // «он хочет забрать?» (GEN `threat` yes/no): 👍 though his moved piece reaches nothing of ours
  | 'notSaved' // after the move a piece of ours is taken, but not the moved one on its new square — a missed save leaves the attacked one «под ударом» («после такого хода нашу фигуру заберут»)
  | 'hisPiece' // «что он хочет?»: touched HIS piece where ours is asked (input slip, not counted — a redirect line)
  | 'notThisWay' // stage 0: the walking piece moved the way it never moves («так эта фигура не ходит» — input, not counted, a redirect line)
  | 'notOurs' // touched an own piece where an opponent piece was asked (input slip, not counted)
  | 'notGuard' // «эта фигура не охраняет» — a guards tap on a piece that cannot take back (chapter 1.1)
  | 'allowsMate' // «после этого хода нам ставят мат» — a riddle from the child's own game (core own.ts, review round 3)
  | 'other';

// ───────────────────────── results (§12.1 p.3–4) ─────────────────────────

/** One task attempt record — fields fixed from v0 (§12.1 p.4). */
export interface TaskOutcome {
  /** task key: content key (`gifts-intro/t3`) or generator key `recipe@version:seed` */
  key: string;
  kind: TaskKind;
  tier: TaskTier;
  skill: SkillId;
  /** a trial («рука-призрак» / «вместе») — never graded, never counted for stars */
  trial: boolean;
  /** solved without a mistake by meaning and without a hint */
  firstTry: boolean;
  /** the first wrong answer (a square, a UCI move, 'yes'/'no', an option number) — null = none */
  firstWrong: string | null;
  firstWrongReason: MistakeReason | null;
  /** mistakes by meaning (finger slips are `misTaps`) */
  mistakes: number;
  misTaps: number;
  /** highest hint level used, 0 = none */
  hint: 0 | 1 | 2 | 3 | 4;
  /** «↻ повторить» presses */
  replays: number;
  /** ms from the task being ready (its prompt finished or skipped) to the first touch; null = no touch */
  firstTouchMs: number | null;
  durationMs: number;
  /** the task came back at the end of the lesson after a mistake */
  retry: boolean;
}

export type Stars = 0 | 1 | 2 | 3;

/**
 * The full result of one step attempt — a FACT (stored as is; «best stars» etc. are rebuildable caches).
 * Idempotent by `resultId`: a resend never duplicates.
 */
export interface NodeResult {
  /** client-made unique id (uuid) — the send queue and the server merge by it */
  resultId: string;
  stepId: StepId;
  /** hash of the step's content as played (content changes do not rewrite history) */
  contentHash: string;
  /** web build id */
  build: string;
  /** the child's local date YYYY-MM-DD */
  localDate: string;
  startedAt: number;
  finishedAt: number;
  /** finished to the end (an exit in the middle is stored too, `completed: false`) */
  completed: boolean;
  stars: Stars;
  /** «Я уже умею!» / placement / parent — the step counts as passed by a jump */
  jump?: 'test' | 'placement' | 'games' | 'parent' | 'below';
  /** muted on purpose for this attempt (G1 «one session without sound») */
  muted?: boolean;
  tasks: TaskOutcome[];
}

// ───────────────────────── progress view (computed, never stored) ─────────────────────────

export type StepKind = 'lesson' | 'practice' | 'minigame' | 'game' | 'review' | 'checkpoint';
export type StepState = 'locked' | 'open' | 'current' | 'done' | 'jumped';

export interface PathStepView {
  id: StepId;
  kind: StepKind;
  /** 1–3 words */
  label: string;
  state: StepState;
  bestStars: Stars;
  /** inserted behind the child after he passed — optional «Новое» */
  isNew?: boolean;
  /** game steps: the bot of the chapter */
  persona?: PersonaId;
}

export interface PathChapterView {
  id: ChapterId;
  stage: PathStage;
  /** 1–3 words */
  label: string;
  icon: string;
  steps: PathStepView[];
  done: boolean;
}

/** GET /path (or computed on the device in P1 from stored NodeResults by core `scheduler`). */
export interface PathProgressView {
  stage: PathStage;
  chapters: PathChapterView[];
  /** the glowing circle — null = everything open is done */
  current: StepId | null;
  /** exam coins and forts of the current stage (§5.5) — the child sees only these, never percentages */
  coins: { have: number; need: number };
  forts: { have: number; need: number };
}

// ───────────────────────── games (§7) ─────────────────────────

/** Per-move gift facts (§7.3) live on `MoveJudgement` as optional `giftAvailable` / `giftTaken` / `ownHung`. */
export type MoveGiftFacts = Pick<import('./contracts.ts').MoveJudgement, 'giftAvailable' | 'giftTaken' | 'ownHung'>;

/** How a path game is played (StepSpec 'game'). */
export interface PathGameSetup {
  persona: PersonaId;
  help: HelpLevel;
  exam: boolean;
  /** theme family of the coach («Подарки» = 'gifts') */
  themeFamily: string;
  /** bot-trainer behaviour (§7.2) on stages 1–2 */
  trainer: boolean;
  /** the child's colour; absent = alternate */
  color?: Color;
}

// ───────────────────────── feature flags (§12.1 p.5) ─────────────────────────

export const FEATURE_KEYS = ['path-v2', 'preview'] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

/** GET /api/features — resolved for the signed-in account: account → invite group → site default → env. Default off. */
export type FeaturesView = Record<FeatureKey, boolean>;

// ───────────────────────── storage (P1: one JSON doc) ─────────────────────────

/**
 * The path's progress on a device: a `PathStoreDoc` JSON in localStorage under this key — since v1 the CACHE of the
 * server's path tables (`/api/path`, apps/web/src/features/path/pathSync.ts), no longer in the account's synced keys
 * (the P1 copy the account kept as `client:gambit.path.v2` is imported by the server once).
 */
export const PATH_STORAGE_KEY = 'gambit.path.v2';

export interface PathStoreDoc {
  v: 1;
  /** facts, merged as a union by `resultId` whenever two copies meet (never last-writer-wins per result) */
  results: NodeResult[];
  /** parent opened everything (§5.1) */
  unlockAll?: boolean;
  /** help level of the next path game per chapter (§7.1), recomputed from games */
  help?: Record<ChapterId, HelpLevel>;
  /** facts of finished path games (§7.1 help ladder: core scheduler `nextHelp`), merged as a union by `gameId` */
  games?: PathGameFact[];
  /** v1: the one skill memory (§11.1, core path/memory.ts) — per skill the later review wins; synced as server cards */
  skills?: SkillCard[];
  /** v1: «ошибка вернётся» cards (§11.1) — per card the later event wins; synced as server cards */
  mistakes?: MistakeCard[];
  /** v1 (PAR): the age band (§5.3) — the only thing stored about the age; a cache of the server's (`PUT /path/meta`) */
  ageBand?: AgeBand;
  /**
   * v1: the server's stage decisions (`PathServerState.decisions`: sign-up «не знаю ходов» → 0, the placement, the
   * parent's manual change, the crown passed — the ONE decision, apps/server/src/services/path.ts) as last pulled — a
   * union by `at` + `to`, oldest first; «Для взрослых» and the stage celebration read them
   */
  decisions?: StageDecision[];
}

/**
 * v1 (TCH): the games of stages 1–2 with the path on and the stickers they earned — a `PathPlayDoc` JSON in
 * localStorage under its OWN key, which follows the account like the other child keys (web `SYNC_KEYS` + the server's
 * `CLIENT_STATE_KEYS`, kept in the account's `kv`, §12.5 «kv — наклейки»). Not inside `PathStoreDoc`: the path's
 * results move to the server's path tables (track SRV), these facts stay a small synced value of their own.
 */
export const PATH_PLAY_STORAGE_KEY = 'gambit.path.play';

export interface PathPlayDoc {
  v: 1;
  /**
   * every finished game of stages 1–2 with the path on — path steps and ordinary games from «Играть» — for the help
   * ladder of ordinary games (§7.1), the fade of the thinking habit (§7.4) and the stickers (§10); union by `gameId`
   */
  played?: PathPlayedFact[];
  /** unexpected stickers for skill shown in games (§10), union by `id` (the first earned wins); never removed */
  stickers?: PathSticker[];
}

/** An unexpected sticker for skill shown in a game (§10): never for finishing a chapter, never promised in advance. */
export interface PathSticker {
  /** core `path/stickers.ts` STICKERS id ('first-fort', 'gifts-10' …) */
  id: string;
  /** when it was earned, epoch ms */
  at: number;
  /** the game that showed the skill */
  gameId?: string;
}

/**
 * One finished game of stages 1–2 with the path on (TCH, §7.1–§7.4, §10). The ladder counts are by the child's OWN moves
 * (§3.10, like `PathGameFact`); the sticker counts by moves made without help shown for them.
 */
export interface PathPlayedFact {
  gameId: string;
  /** finish time, epoch ms */
  at: number;
  /** the help of the game ('exam' = none) */
  help: HelpLevel | 'exam';
  /** the path step the game belonged to (absent = an ordinary game) */
  stepId?: StepId;
  /** the thinking habit ran in this game (its glow fades by the count of such games) */
  habit: boolean;
  /** ladder counts (own moves, §3.10) */
  giftsTaken: number;
  giftChances: number;
  ownHung: number;
  /** finished: any result but «*» */
  finished: boolean;
  won: boolean;
  /** sticker counts (moves without help shown for them): gifts taken, safe checks, mates */
  gifts: number;
  safeChecks: number;
  mates: number;
  /** own pieces ≥ 3 left as a gift in the whole game (any move), and the child's moves */
  hungAll: number;
  moves: number;
  /**
   * the step of the ordinary games' help ladder this game was played on (the level it recommended then, core
   * played.ts `ladderHelp`) — the ladder steps from it by the rules of §7.1, never from the style the child picked;
   * absent: a game stored before it, or a bullet game
   */
  ladder?: HelpLevel;
  /** a 1-minute game (the coach is silent there): it never moves the ladder */
  bullet?: true;
  /** the child resigned (a fort is a game played through, §3.10) */
  resigned?: true;
}

/** One finished path game — what the help ladder needs (counts by the child's own moves only, §3.10). */
export interface PathGameFact {
  stepId: StepId;
  /** the game record's id (the review's id) */
  gameId: string;
  help: HelpLevel;
  exam: boolean;
  giftsTaken: number;
  giftChances: number;
  ownHung: number;
  /** finish time, epoch ms */
  at: number;
  /** v1: the stage the game was played at (coins / forts belong to that stage) */
  stage?: PathStage;
  /** v1: plies of the child's OWN `isGift` captures (an exam game gives a coin each, §5.5) */
  giftPlies?: number[];
  /** v1: plies where the child had an own `isGift` capture chance (the ≥ 70 % over ≥ 20 rule) */
  chancePlies?: number[];
  /** v1: mate in one found by the child's own move (stage 2 coins «матов», later) */
  mateIn1Plies?: number[];
  /** v1: the game was finished (any result but «*») */
  finished?: boolean;
  /**
   * v1: what «played through» needs (core stage.ts `isFort`, one rule with the «Первая крепость» sticker): the child won,
   * his moves in the game, he resigned. A fort = an exam game played through (won or ≥ 20 own moves, not resigned) with
   * ≤ 1 own piece hung. Absent on older facts (judged by `finished` alone).
   */
  won?: boolean;
  moves?: number;
  resigned?: true;
  /**
   * fixer r1: how `giftChances` / `giftsTaken` / `ownHung` were counted. 'gift' = per GIFT (core path/gameFacts.ts): a
   * piece left as a gift for five moves is one chance (taken or missed), a piece of ours hung and left hanging is one
   * drop, a move that mates misses nothing — the stage rule then reads these counts; `chancePlies` is the evidence (the
   * ply each chance appeared). Absent: per ply (v1 facts, the rule counts `chancePlies ∪ giftPlies`).
   */
  counting?: 'gift';
}

/** A piece value table (§6.6 keepAll; «кто дороже» 1/3/3/5/9). The king has no material value. */
export const PATH_PIECE_VALUE: Record<PieceType, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

// ───────────────────────── stage decision (§5.5, one rule) ─────────────────────────

export const STAGE_RULE = { coins: 10, forts: 3, giftShare: 0.7, minChances: 20 } as const;

/** Why the stage changed — the ONE decision (core `decideStage`, server services/path.ts), always with a reason. */
export type StageReason = 'path+play' | 'placement' | 'manual';

export interface StageDecision {
  from: PathStage;
  to: PathStage;
  reason: StageReason;
  at: number;
  /** numbers the decision saw — for «Для взрослых» */
  detail?: { coins: number; forts: number; giftShare: number | null; chances: number };
}

/** What the child sees of the crown (§5.5): coins and forts only, never percentages. */
export interface StageCrownView {
  stage: PathStage;
  coins: { have: number; need: number };
  forts: { have: number; need: number };
  /** the crown is full but the share is not there yet → Гамбитик calls for one more exam game */
  wantsExam: boolean;
  passed: boolean;
}

// ───────────────────────── one skill memory (§11.1) ─────────────────────────

/** One FSRS card per skill — shared by the path, Lichess puzzles, «Учитель» and the parent report. */
export interface SkillCard {
  skill: SkillId;
  /** epoch ms; null = never reviewed */
  due: number | null;
  stability: number;
  difficulty: number;
  reps: number;
  lapses: number;
  lastReview: number | null;
  /** last results (1 = right first try, 0 = not), newest last, ≤ 12 */
  recent: (0 | 1)[];
  /**
   * v1.1 MEM: the review of the session of `lastReview` (the child's day) so far — every graded result of that day grades
   * it, not only the first. Absent on older copies: a later result that day then only adds to `recent`.
   */
  session?: SkillSessionReview;
}

/** One session's FSRS review in progress: the memory before it and the session's tally. */
export interface SkillSessionReview {
  /** the FSRS numbers before the session's review */
  base: Pick<SkillCard, 'due' | 'stability' | 'difficulty' | 'reps' | 'lapses' | 'lastReview'>;
  /** the sum of the session's graded results (1 right at the first try, ½ a puzzle solved with hints, 0 not) */
  score: number;
  /** how many graded results the session had */
  count: number;
}

/** «Ошибка вернётся» (§11.1): a separate card per skill × task kind × tier, never inside the puzzle block. */
export interface MistakeCard {
  id: string;
  skill: SkillId;
  kind: TaskKind;
  tier: TaskTier;
  /** the task key that went wrong (content key or `recipe@v:seed`) */
  taskKey: string;
  due: number;
  reps: number;
  createdAt: number;
}

// ───────────────────────── profile additions (§5.3) ─────────────────────────

/** Only the band is stored (data minimisation); it changes line length, board size and session length — nothing else. */
export type AgeBand = '5-6' | '7-8' | '9+';

// ───────────────────────── server (§12.5) ─────────────────────────

/** GET /api/path — the facts the server holds (the view is computed by core on the client too). */
export interface PathServerState {
  stage: PathStage;
  results: NodeResult[];
  games: PathGameFact[];
  decisions: StageDecision[];
  skills: SkillCard[];
  mistakes: MistakeCard[];
  unlockAll: boolean;
  ageBand: AgeBand | null;
  /**
   * §8.6, only while the server requires recorded voice (GAMBIT_PATH_REQUIRE_VOICE=1): lessonId → every wording of its
   * pools has a recorded take. Absent = show every lesson; a lesson false (or missing) here is «скоро».
   */
  ready?: Record<string, boolean>;
  /**
   * opaque: `GET /api/path?since=<cursor>` sends only the results / games stored after it and the cards changed after
   * it (review round 2; the settings whole, the newest decisions)
   */
  cursor?: string;
  /** results / games are one page: more wait after `cursor` (ask again with it) */
  more?: boolean;
  /** the stage crown as the ONE rule counts it now (core `crownView`): what the map's «копилка» shows */
  crown?: StageCrownView;
}

/** POST /api/path/nodes/:stepId/result — idempotent by resultId. */
export interface PathResultResponse {
  ok: true;
  duplicate: boolean;
  /** set when this result (or game) completed the stage — the ONE decision */
  decision?: StageDecision;
}

/** POST /api/path/import — a backlog of facts in one go (a browser's local doc meets the server); each item on its own. */
export interface PathImportRequest {
  results: NodeResult[];
  games: PathGameFact[];
}

export interface PathImportResponse {
  ok: true;
  /** new facts stored */
  added: { results: number; games: number };
  /** already there (idempotent) */
  duplicates: number;
  /** ids the server refused (malformed) — never resent */
  rejected: string[];
  decision?: StageDecision;
}

/** PUT /api/path/meta — the parent's «Открыть всё» (§5.1) and the age band (§5.3); absent = unchanged. */
export interface PathMetaUpdate {
  unlockAll?: boolean;
  ageBand?: AgeBand | null;
}

/** POST /api/path/jump/:chapterId — the chapter counts as a «прыжок» (every step not passed yet gets a jump result). */
export interface PathJumpRequest {
  kind?: 'parent' | 'placement' | 'games' | 'test';
}

export interface PathJumpResponse {
  ok: true;
  /** the jump results made now (merge them like any result) */
  results: NodeResult[];
  decision?: StageDecision;
}

/**
 * POST /api/path/placement — once, at sign-up (§5.3): the stage (0 = does not know the moves) and the chapters both of
 * whose tasks were right. 409 `placement-closed` for a child with a stage decision, path facts or a stage above 1.
 */
export interface PathPlacementRequest {
  stage: PathStage;
  jumped: ChapterId[];
}
